<script>
  import {
    deleteViewSpec,
    activeSpec,
    unlockedSpecs,
    updateUnlockedSpecs,
    updateActiveSpec,
    flushActiveSpec,
    systemLog,
    soloUnlock,
    unlockSpec,
    lockSpec,
    setSoloUnlock,
    clearSoloUnlock,
    unlockedSpecsMemory,
    setUnlockedSpecsMemory,
    showRuler,
    currMenu,
  } from "../editor/storage.js";

  // app system features
  import { alertYesNo } from "../alert.js";
  import {
    moveToArtboard,
    afterDeleteArtboard,
  } from "../editor/component/artboard.js";
  import { openSystemTooltip, closeSystemTooltip } from "../editor/utility.js";

  import History from "./component/artboardHistory.svelte";
  import Versions from "./component/artboardVersions.svelte";

  // direct manipulation (todo)
  import { goToMenu, cancelDirectContext } from "../editor/direct.js";

  export let spec;
  export let inputFocusAction;
  export let inputBlurAction;

  function artboardClickFunction() {
    cancelDirectContext();
  }
  // artboard management (activity, lock, delete)
  // activity
  function setActive(u) {
    if ($activeSpec !== u && $unlockedSpecs.includes(u)) {
      document
        .querySelectorAll(`#view-${$activeSpec} *.dupo-g-found`)
        .forEach((elem) => elem?.classList.remove("dupo-g-found"));
      activeSpec.set(u);
      updateActiveSpec(u);
      $systemLog.log({ message: "Activate an artboard", artboard_uid: u });
    }
  }
  function setInactive(u) {
    activeSpec.set(-1);
    flushActiveSpec();
    $systemLog.log({ message: "Deactivate an artboard", artboard_uid: u });
  }
  // locks
  function isUnlocked(uid) {
    return $unlockedSpecs.includes(uid);
  }
  function togglArtboardLock(uid) {
    if ($soloUnlock !== undefined) {
      // solo-unlock => two locks;
      let old_uid = $soloUnlock;
      unlockSpec(uid);
      $systemLog.log({ message: "Unlock an artboard", artboard_uid: uid });
      clearSoloUnlock();
      $systemLog.log({
        message: "Solo-unlock an artboard",
        artboard_uid: old_uid,
      });
    } else if (isUnlocked(uid)) {
      // unlocked => locked
      lockSpec(uid);
      if ($activeSpec === uid) setInactive(uid);
      $systemLog.log({ message: "Lock an artboard", artboard_uid: uid });
    } else {
      // locked => unlocked
      unlockSpec(uid);
      $systemLog.log({ message: "Unlock an artboard", artboard_uid: uid });
    }
  }
  // delete
  function deleteArtboard(uid, name) {
    if ($activeSpec !== uid) return;
    let onYes = new CustomEvent("onYes_" + uid, {
      detail: { outcome: "yes" },
    });
    let onNo = new CustomEvent("onNo_" + uid, {
      detail: { outcome: "no" },
    });
    alertYesNo({
      title: "Delete an artboard",
      body: "Do you really want to delete Artboard " + name + "?",
      onYes: onYes,
      onNo: onNo,
    });
    function yesHandler(e) {
      document.body.removeEventListener("onYes_" + uid, yesHandler);
      document.body.removeEventListener("onNo_" + uid, noHandler);
      unlockedSpecs.update((items) => {
        return items.filter((d) => d !== uid);
      });
      setInactive(uid);
      unlockSpec(uid);
      afterDeleteArtboard(uid);
      deleteViewSpec(uid);
      $systemLog.log({ message: "Delete an artboard", artboard_uid: uid });
    }
    function noHandler(e) {
      document.body.removeEventListener("onYes_" + uid, yesHandler);
      document.body.removeEventListener("onNo_" + uid, noHandler);
    }
    document.body.addEventListener("onYes_" + uid, yesHandler, { once: true });
    document.body.addEventListener("onNo_" + uid, noHandler, { once: true });
  }
  // solo mute
  function toggleSolo(uid) {
    if ($soloUnlock === undefined) {
      // nothing is solo-unlocked
      setSoloUnlock(uid);
      setUnlockedSpecsMemory($unlockedSpecs);
      updateUnlockedSpecs([uid]);
      $systemLog.log({ message: "Set solo", artboard_uid: uid });
    } else if ($soloUnlock !== undefined && $soloUnlock !== uid) {
      // there's existing solo-unlock
      setSoloUnlock(uid);
      updateUnlockedSpecs([uid]);
      $systemLog.log({ message: "Set solo", artboard_uid: uid });
    } else {
      // releasing from solo-unlock
      let old_uid = $soloUnlock;
      clearSoloUnlock();
      updateUnlockedSpecs($unlockedSpecsMemory);
      $systemLog.log({ message: "Unset solo", artboard_uid: old_uid });
    }
  }
  // statsu bar
  let viewVersions = false;
  let viewHistory = false;
  function statusBarControl(type, value) {
    if (type === "versions") {
      viewVersions = value;
      if (value) viewHistory = false;
    } else if (type === "history" && value) {
      if (value) viewVersions = false;
      viewHistory = value;
    }
  }
  currMenu.subscribe((menu) => {
    if (menu) {
      viewVersions = false;
      viewHistory = false;
    }
  });
  unlockedSpecs.subscribe((unlocked) => {
    if (!unlocked.includes(spec.uid)) {
      viewVersions = false;
      viewHistory = false;
    }
  });
</script>

<div
  id={"dupo-view-" + spec.uid}
  class={"dupo-view mixed" +
    ($activeSpec === spec.uid ? " active" : "") +
    ($unlockedSpecs.includes(spec.uid) ? " unlocked" : " locked")}
  on:click={() => {
    artboardClickFunction();
    setActive(spec.uid);
  }}
  on:mouseover={function (event) {
    if ($unlockedSpecs.includes(spec.uid)) return;
    openSystemTooltip(event, "To activate, unlock this artboard first.");
  }}
  on:mousemove={function (event) {
    if ($unlockedSpecs.includes(spec.uid)) return;
    openSystemTooltip(event, "To activate, unlock this artboard first.");
  }}
  on:focus={function (event) {
    if ($unlockedSpecs.includes(spec.uid)) return;
    openSystemTooltip(event, "To activate, unlock this artboard first.");
  }}
  on:blur={() => closeSystemTooltip()}
  on:mouseout={() => closeSystemTooltip()}
>
  <!-- title -->
  <div
    class="dupo-view-title"
    on:dblclick={(event) => {
      if (!event.target.getAttribute("data-prevent-move") !== "true")
        moveToArtboard(spec.uid);
    }}
  >
    <h3 class="h7">
      <span class="activity"
        ><i
          class={`bi bi-${$activeSpec === spec.uid ? "circle-fill" : "circle"}`}
        /></span
      ><span>{spec.artboard.name}</span>
    </h3>
    <div class="artboard-options" data-prevent-move="true">
      <button
        type="button"
        data-role="manage"
        alt="manage this altboard"
        data-prevent-move="true"
        on:click={() => {
          if ($activeSpec !== spec.uid) return;
          goToMenu({ first: "artboard", subMenu: "editArtboard" });
          $systemLog.log({
            message: "(DM) Load artboard control menu",
            artboard_uid: spec.uid,
          });
        }}
        on:mouseover={function (event) {
          openSystemTooltip(
            event,
            "Manage this artboard" +
              `${
                !$unlockedSpecs.includes(spec.uid)
                  ? ". To manage, unlock this artboard first."
                  : ""
              }`
          );
        }}
        on:mousemove={function (event) {
          openSystemTooltip(
            event,
            "Manage this artboard" +
              `${
                !$unlockedSpecs.includes(spec.uid)
                  ? ". To manage, unlock this artboard first."
                  : ""
              }`
          );
        }}
        on:focus={function (event) {
          openSystemTooltip(
            event,
            "Manage this artboard" +
              `${
                !$unlockedSpecs.includes(spec.uid)
                  ? ". To manage, unlock this artboard first."
                  : ""
              }`
          );
        }}
        on:blur={() => closeSystemTooltip()}
        on:mouseout={() => closeSystemTooltip()}
      >
        <i class="bi bi-gear-fill" data-prevent-move="true" />
      </button>
      <button
        type="button"
        data-role="lock-indicator"
        alt="lock this artboard"
        data-prevent-move="true"
        on:click={() => {
          togglArtboardLock(spec.uid);
        }}
        on:mouseover={function (event) {
          openSystemTooltip(
            event,
            $unlockedSpecs.includes(spec.uid)
              ? "Lock this artboard"
              : "Unlock this artboard"
          );
        }}
        on:mousemove={function (event) {
          openSystemTooltip(
            event,
            $unlockedSpecs.includes(spec.uid)
              ? "Lock this artboard"
              : "Unlock this artboard"
          );
        }}
        on:focus={function (event) {
          openSystemTooltip(
            event,
            $unlockedSpecs.includes(spec.uid)
              ? "Lock this artboard"
              : "Unlock this artboard"
          );
        }}
        on:blur={() => closeSystemTooltip()}
        on:mouseout={() => closeSystemTooltip()}
      >
        <i
          class={`bi bi-${
            $unlockedSpecs.includes(spec.uid) ? "unlock-fill" : "lock"
          }`}
          data-prevent-move="true"
        />
      </button>
      <button
        type="button"
        data-role="solo-indicator"
        alt="solo this artboard"
        data-prevent-move="true"
        on:click={() => {
          toggleSolo(spec.uid);
        }}
        on:mouseover={function (event) {
          openSystemTooltip(
            event,
            $soloUnlock === spec.uid
              ? "Solo this artboard"
              : "Un-solo this artboard"
          );
        }}
        on:mousemove={function (event) {
          openSystemTooltip(
            event,
            $soloUnlock === spec.uid
              ? "Solo this artboard"
              : "Un-solo this artboard"
          );
        }}
        on:focus={function (event) {
          openSystemTooltip(
            event,
            $soloUnlock === spec.uid
              ? "Solo this artboard"
              : "Un-solo this artboard"
          );
        }}
        on:blur={() => closeSystemTooltip()}
        on:mouseout={() => closeSystemTooltip()}
      >
        <span
          class={$soloUnlock === spec.uid ? "solo-on" : "solo-off"}
          data-prevent-move="true">S</span
        >
      </button>
      <button
        type="button"
        data-role="removal"
        alt="delete this artboard"
        data-prevent-move="true"
        on:click={() => {
          deleteArtboard(spec.uid, spec.artboard.name);
        }}
        on:mouseover={function (event) {
          openSystemTooltip(
            event,
            "Delete this artboard" +
              `${
                !$unlockedSpecs.includes(spec.uid)
                  ? ". To delete, unlock this artboard first."
                  : ""
              }`
          );
        }}
        on:mousemove={function (event) {
          openSystemTooltip(
            event,
            "Delete this artboard" +
              `${
                !$unlockedSpecs.includes(spec.uid)
                  ? ". To delete, unlock this artboard first."
                  : ""
              }`
          );
        }}
        on:focus={function (event) {
          openSystemTooltip(
            event,
            "Delete this artboard" +
              `${
                !$unlockedSpecs.includes(spec.uid)
                  ? ". To delete, unlock this artboard first."
                  : ""
              }`
          );
        }}
        on:blur={() => closeSystemTooltip()}
        on:mouseout={() => closeSystemTooltip()}
      >
        <i class="bi bi-trash-fill" data-prevent-move="true" />
      </button>
    </div>
  </div>

  <!-- view -->
  <div id={spec.domId} class="view-wrapper" />

  <!-- rulers -->
  <div
    id={"ruler-top-" + spec.uid}
    class={"ruler-top ruler " + ($showRuler ? "" : " hidden")}
  >
    <div class="mark_left_limit" />
    <div class="mark_right_limit">Device width</div>
  </div>
  <div
    id={"ruler-left-" + spec.uid}
    class={"ruler-left ruler " + ($showRuler ? "" : " hidden")}
  >
    <div class="mark_top_limit" />
    <div class="mark_bottom_limit">Device height</div>
  </div>

  <!-- status -->
  <div class="dupo-view-status">
    <div class="status">
      <i class="bi bi-info-circle" />{$activeSpec === spec.uid
        ? "active"
        : "inactive"}/{$unlockedSpecs.includes(spec.uid)
        ? "unlocked"
        : "locked"}
    </div>
    <div class="controls" style="display: flex; gap: 0;">
      <History
        history={spec.history}
        uid={spec.uid}
        view={viewHistory}
        {statusBarControl}
      />
      <Versions view={viewVersions} {spec} {statusBarControl} />
    </div>
  </div>
</div>

<style>
  /* dupo view wrapper */
  .dupo-view {
    display: inline-block;
    margin: 1rem;
    opacity: 0.8;
    border-radius: var(--default-radius);
    position: relative;
    z-index: 10;
  }
  .dupo-view.active.unlocked {
    opacity: 1;
  }
  .dupo-view.locked {
    opacity: 0.3;
  }
  .dupo-view.active {
    z-index: 30;
  }
  /* view title */
  .dupo-view .dupo-view-title {
    display: flex;
    justify-content: space-between;
    border: 0;
    border-radius: var(--default-radius) var(--default-radius) 0 0;
    user-select: none;
    cursor: default;
    background-color: #f3f3f3;
  }
  /* title activity display */
  .dupo-view .dupo-view-title .activity {
    display: block;
    margin-top: -1px;
  }
  /* title text */
  .dupo-view .dupo-view-title .h7 {
    margin-bottom: 0 !important;
    margin-right: 0.5rem;
    padding: 0.25rem;
    display: flex;
    justify-content: start;
    font-size: 0.9rem;
  }
  .dupo-view .dupo-view-title .h7 i {
    font-size: 0.65rem;
    margin: 0 0.35rem 0 0.15rem;
  }
  /* option buttons */
  .dupo-view .dupo-view-title .artboard-options {
    margin-bottom: 0 !important;
    padding: 0;
    display: flex;
    justify-content: end;
  }
  :global(.dupo-view .dupo-view-title .artboard-options button) {
    appearance: none;
    margin: 0 !important;
    border: 0;
    background: transparent;
    color: #454545;
    font-size: 0.8rem;
  }
  :global(.dupo-view .dupo-view-title .artboard-options button:hover) {
    color: #000000;
    background: rgba(0, 0, 0, 0.085);
  }

  .solo-on,
  .solo-off {
    font-family: var(--monospace-font);
    border: 1px solid #454545;
    border-radius: var(--default-radius);
    padding: 0.15rem 0.3rem;
    font-size: 0.7rem;
  }
  .solo-on {
    font-weight: 900;
    background-color: #454545;
    color: white;
  }
  .solo-off {
    font-weight: 100;
  }
  .dupo-view.active .dupo-view-title .artboard-options button:hover .solo-on {
    color: white;
    background-color: #f8801d;
    border-color: #f8801d;
  }
  .dupo-view.active .dupo-view-title .artboard-options button:hover .solo-off {
    color: #f8801d;
    border-color: #f8801d;
  }

  /* active shadow */
  .dupo-view.active {
    box-shadow: 0 0 15px rgba(0, 0, 0, 0.1), 0 7px 25px rgba(0, 0, 0, 0.1),
      0 15px 35px rgba(0, 0, 0, 0.1);
    z-index: 9;
  }

  /* active title */
  .dupo-view.active .dupo-view-title {
    font-weight: var(--semi-bold-font-weight) !important;
    background-color: #ffc422 !important;
  }
  .dupo-view.active .dupo-view-title .artboard-options button:hover {
    color: #f8801d;
    background-color: rgba(254, 194, 49, 0.3) !important;
  }

  /* content */
  .dupo-view .view-wrapper {
    display: block;
    padding: 1rem;
    margin: 0;
    font-family: "Arial";
    border: 0;
    background: #ffffff;
    overflow: hidden;
  }

  /* view status */
  .dupo-view .dupo-view-status {
    display: flex;
    justify-content: space-between;
    border-radius: 0 0 var(--default-radius) var(--default-radius);
    background: #f3f3f3;
    border: 0;
  }
  .dupo-view-status .status {
    display: block;
    font-size: 0.75rem;
    padding: 0.15rem 0.5rem;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }
  .dupo-view-status .status i.bi-info-circle {
    margin: 0 0.25rem 0 0;
    /* font-size: 0.65rem !important; */
  }

  /* exploration */
  .exploration-button-wrap {
    position: absolute;
    left: 0;
    bottom: 0;
    transform: translateY(calc(100% + 1rem));
    max-width: 100%;
  }
  .rec-button {
    appearance: none;
    padding: 0.5rem 1rem;
    border-radius: var(--default-radius);
    color: white;
    font-size: 0.9rem;
    font-weight: 600;
    border: 0;
    box-shadow: 0 0 15px rgba(0, 0, 0, 0.1), 0 7px 25px rgba(0, 0, 0, 0.1);
    margin-bottom: 0.5rem;
    text-align: left;
    z-index: 10;
  }
  .rec-button span {
    display: inline-block;
    vertical-align: top;
  }
  .rec-button:hover {
    box-shadow: 0 0 15px rgba(0, 0, 0, 0.1), 0 7px 25px rgba(0, 0, 0, 0.1),
      0 15px 35px rgba(0, 0, 0, 0.1);
  }
  .rec-button.opened {
    background-color: #a97811;
  }
  .exploration-button {
    background-color: #005996;
  }
  .morelikethis-button {
    background-color: #00796a;
  }
  .augmentation-button-wrap {
    border-radius: var(--default-radius);
    border: 0;
    box-shadow: 0 0 15px rgba(0, 0, 0, 0.1), 0 7px 25px rgba(0, 0, 0, 0.1);
    margin-bottom: 0.5rem;
    background-color: #eeeeee;
    overflow: hidden;
    padding: 0;
  }
  .augmentation-button-wrap:hover {
    box-shadow: 0 0 15px rgba(0, 0, 0, 0.1), 0 7px 25px rgba(0, 0, 0, 0.1),
      0 15px 35px rgba(0, 0, 0, 0.1);
  }
  .augmentation-button-wrap .buttons {
    width: 100%;
    display: flex;
    justify-content: stretch;
  }
  .augmentation-button-wrap button {
    appearance: none;
    width: 25%;
    padding: 0.5rem 0.2rem;
    color: #454545;
    font-size: 0.75rem;
    border: 0;
    background: transparent;
    text-align: center;
    display: block;
  }
  .augmentation-button-wrap button:hover {
    background-color: rgba(0, 0, 0, 0.1);
  }
  .augmentation-button-wrap .name {
    padding: 0.5rem 1rem;
    width: auto;
    text-align: left;
    text-overflow: ellipsis;
    font-size: 0.85rem;
    border-bottom: 1px solid #ccc;
  }
  /* ruler */
  .ruler.hidden {
    display: none;
  }
  .ruler-top,
  .ruler-left {
    position: absolute;
    overflow: hidden;
    padding: 0;
  }
  .ruler-top {
    height: 1rem;
    top: 27.23px;
    left: 1rem;
  }
  .ruler-left {
    width: 1rem;
    top: calc(27.23px + 1rem);
    left: 0;
  }
  .mark_bottom_limit,
  .mark_right_limit {
    position: absolute;
    font-size: 0.7rem;
    color: #999999;
    font-weight: 500;
    width: 100px;
  }
  .mark_top_limit,
  .mark_left_limit {
    position: absolute;
    top: 0;
    left: 0;
    height: 1rem;
    width: 1rem;
  }
  .mark_bottom_limit {
    left: 0;
    transform-origin: 0 0;
    transform: rotate(-90deg);
    border-left: 2px solid #dddddd;
    padding-left: 0.5rem;
  }
  .mark_right_limit {
    top: 0;
    text-align: right;
    border-right: 2px solid #dddddd;
    padding-right: 0.5rem;
  }
  .mark_top_limit {
    border-top: 2px solid #dddddd;
  }
  .mark_left_limit {
    border-left: 2px solid #dddddd;
  }
  .dupo-view.active .mark_bottom_limit {
    border-left: 2px solid #ffc422;
  }
  .dupo-view.active .mark_right_limit {
    border-right: 2px solid #ffc422;
  }
  .dupo-view.active .mark_top_limit {
    border-top: 2px solid #ffc422;
  }
  .dupo-view.active .mark_left_limit {
    border-left: 2px solid #ffc422;
  }
</style>
